import React, { useEffect, useRef, useMemo } from 'react';
import { FocusHistory } from '../utils/focusScore';
import { getDistractionColor } from '../utils/colors';

export interface TimelineEvent {
  status: string;
  startTime: number;
  endTime: number;
  label: string;
}

export const calculateProjection = (history: FocusHistory, startTime?: number, plannedDuration?: number) => {
  if (!startTime || !plannedDuration) return null;
  if (!history || history.length === 0) return null;
  const plannedEnd = startTime + plannedDuration;
  if (Date.now() < plannedEnd) return null;
  const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);
  const lastTs = sorted[sorted.length - 1]?.timestamp;
  if (!lastTs || plannedEnd - lastTs <= 30000) return null;
  return { startTime: lastTs, endTime: plannedEnd };
};

export const processTimelineEvents = (history: FocusHistory, startTime?: number, plannedDuration?: number) => {
    if (!history || history.length === 0) return [];
    
    const events: TimelineEvent[] = [];
    let currentEvent: TimelineEvent | null = null;
    
    // Sort history by timestamp just in case
    const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);

    if (typeof startTime === 'number' && startTime > 0) {
      const firstTs = sorted[0]?.timestamp;
      if (typeof firstTs === 'number' && firstTs - startTime > 30000) {
        events.push({
          status: 'not_focused',
          startTime,
          endTime: firstTs,
          label: 'Partial Attendance'
        });
      }
    }
    
    sorted.forEach((sample, i) => {
      // Determine label
      let label = 'Focused';
      if (sample.state !== 'focused') {
         // Map internal details to display labels
         const d = sample.details || 'unknown';
         if (d === 'phone') label = 'Phone';
         else if (d === 'away') label = 'Away';
         else if (d === 'camera_off') label = 'Camera Off';
         else if (d === 'drowsy') label = 'Drowsy';
         else if (d === 'posture') label = 'Posture';
         else if (d === 'not_focused') label = 'Distracted';
         else label = d.charAt(0).toUpperCase() + d.slice(1);
      }
      
      if (!currentEvent) {
        currentEvent = {
          status: sample.state,
          startTime: sample.timestamp,
          endTime: sample.timestamp,
          label
        };
      } else {
        // Check if status changed or gap is too large (> 65s)
        const timeDiff = currentEvent ? sample.timestamp - currentEvent.endTime : 0;
        const isGap = timeDiff > 30000;

        if (currentEvent && (currentEvent.label !== label || isGap)) {
          // If there was a gap, insert a Partial Attendance event first
          if (isGap) {
               // Close current event
               events.push(currentEvent);
               
               // Add Partial Attendance event
               events.push({
                   status: 'not_focused',
                   startTime: currentEvent.endTime,
                   endTime: sample.timestamp,
                   label: 'Partial Attendance'
               });
               
               // Start new event
               currentEvent = {
                  status: sample.state,
                  startTime: sample.timestamp,
                  endTime: sample.timestamp,
                  label
               };
          } else {
              // Just status change
              events.push(currentEvent);
              currentEvent = {
                  status: sample.state,
                  startTime: sample.timestamp,
                  endTime: sample.timestamp,
                  label
              };
          }
        } else {
          // Extend current
          if (currentEvent) currentEvent.endTime = sample.timestamp;
        }
      }
    });
    
    if (currentEvent) {
      events.push(currentEvent);
    }

    // Check for projection (end of session gap)
    if (startTime && plannedDuration && events.length > 0) {
        const lastEvent = events[events.length - 1];
        const plannedEnd = startTime + plannedDuration;
        
        // Only show if planned duration has elapsed (historical view or completed session)
        if (Date.now() >= plannedEnd) {
             if (plannedEnd - lastEvent.endTime > 30000) {
                 events.push({
                     status: 'not_focused',
                     startTime: lastEvent.endTime,
                     endTime: plannedEnd,
                     label: 'Partial Attendance'
                 });
             }
        }
    }
    
    return events;
};

export const FocusTimeline: React.FC<{ history: FocusHistory; startTime?: number; plannedDuration?: number }> = ({ history, startTime, plannedDuration }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Calculate events for text log
  const timelineEvents = useMemo(() => processTimelineEvents(history, startTime, plannedDuration), [history, startTime, plannedDuration]);

  useEffect(() => {
    const render = () => {
        const cvs = canvasRef.current;
        if (!cvs) return;
        const ctx = cvs.getContext('2d');
        if (!ctx) return;
        
        // Support high-DPI displays
        const dpr = window.devicePixelRatio || 1;
        const rect = cvs.getBoundingClientRect();
        if (rect.width === 0) return;

        cvs.width = rect.width * dpr;
        cvs.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        
        const w = rect.width;
        const h = rect.height;
        ctx.clearRect(0, 0, w, h);
        
        if (!history || history.length === 0) return;
        
        const timelineHeight = 20;
        
        // Use provided startTime if available, else first sample
        const tStart = startTime || history[0].timestamp;
        // Use plannedDuration if available to determine scale
        const duration = plannedDuration || (history[history.length - 1].timestamp - tStart);
        // Ensure duration is at least somewhat positive
        const totalDuration = Math.max(duration, 60000); 
        
        // Draw timeline bars from processed events (handles gaps correctly)
        timelineEvents.forEach((ev, i) => {
           const offset = ev.startTime - tStart;
           // If event started before tStart (unlikely if tStart is history[0]), clamp
           const x = Math.max(0, (offset / totalDuration) * w);
           
           let duration = ev.endTime - ev.startTime;
           
           // For the last event, apply the "live" extension logic
           // If the session is seemingly active (last event is recent), extend it visually up to 30s
           if (i === timelineEvents.length - 1) {
              const now = Date.now();
              const timeSinceEnd = Math.max(0, now - ev.endTime);
              // Only extend if it's within the gap threshold
              const extension = Math.min(timeSinceEnd, 30000);
              duration += extension;
           }

           const width = (duration / totalDuration) * w;
           
           // Ensure at least 1px for visibility if it exists
           const finalWidth = Math.max(1, width);
           
           // Get color based on label
           ctx.fillStyle = getDistractionColor(ev.label);
           
           ctx.fillRect(x, 0, finalWidth, timelineHeight);
        });
        
        // Draw Axis
        ctx.fillStyle = '#6c757d';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        // Determine tick interval
        const durationMin = totalDuration / 60000;
        let tickIntervalMin = 1;
        if (durationMin > 10) tickIntervalMin = 5;
        if (durationMin > 30) tickIntervalMin = 10;
        if (durationMin > 60) tickIntervalMin = 15;
        
        // Draw ticks
        const endMin = Math.ceil(durationMin);
        
        for (let m = 0; m <= endMin; m += tickIntervalMin) {
            const timeOffsetMs = m * 60000;
            if (timeOffsetMs > totalDuration && m !== 0) continue; 
            
            const pct = totalDuration > 0 ? timeOffsetMs / totalDuration : 0;
            if (pct > 1.01) continue; // Allow slight overflow for end tick
            
            const x = Math.min(pct * w, w); // Clamp to edge
            
            // Draw tick line
            ctx.beginPath();
            ctx.moveTo(x, timelineHeight);
            ctx.lineTo(x, timelineHeight + 4);
            ctx.stroke();
            
            // Draw label
            // Adjust position if at edge
            let tx = x;
            let align: CanvasTextAlign = 'center';
            if (x < 10) align = 'left';
            else if (x > w - 10) align = 'right';
            
            ctx.textAlign = align;
            ctx.fillText(`${m}m`, tx, timelineHeight + 6);
        }
    };

    render();
    window.addEventListener('resize', render);
    const tid = setTimeout(render, 500);
    return () => {
        window.removeEventListener('resize', render);
        clearTimeout(tid);
    };
  }, [history, startTime, plannedDuration, timelineEvents]);

  return (
    <div className="d-flex flex-column gap-2">
      <div style={{ width: '100%', height: '40px', background: 'transparent' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      </div>
      
      {/* Text Log - Hidden per user request, but data is preserved internally in history */}
      {/* {(startTime && plannedDuration) && (
        <div className="timeline-log small text-muted border-top pt-2 mt-1">
          <div className="mb-1">
             Session started at {format(startTime, 'h:mma')} with planned duration of {Math.round(plannedDuration / 60000)} min
          </div>
          {timelineEvents.map((ev, i) => (
             <div key={i} className="mb-1">
               <span className="fw-bold" style={{ color: ev.status === 'focused' ? 'var(--success-color, green)' : 'var(--bs-danger)' }}>
                 {ev.label}
               </span> - {format(ev.startTime, 'h:mma')} to {format(ev.endTime, 'h:mma')}
             </div>
          ))}
          {projection && (
             <div className="mb-1 text-danger">
               <span className="fw-bold">Projected Partial Attendance</span> - {format(projection.startTime, 'h:mma')} to {format(projection.endTime, 'h:mma')}
             </div>
          )}
          <div className="mt-1 border-top pt-1">
             Session ended at {format(startTime + plannedDuration, 'h:mma')}
          </div>
        </div>
      )} */}
    </div>
  );
};
